import { useEffect, useRef, useCallback, useState } from "react";
import { TradingSymbol } from "@/utils/mockData";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { config } from '../config';
import fetchSymbolsFromAPI from './fetchSymbolsFromAPI';
import { isTokenExpired, isTokenExpiringSoon } from '../utils/jwtUtils';

const WS_URL = config.socketUrl;

interface TickData {
  sm: string; 
  b: number; 
  a: number; 
  t: number; 
  tu: number;
  bpc: number; 
  apc: number; 
  mc: number;
  b1h: number; 
  b1c: number; 
  b1l: number; 
  b1o: number;
  chp: number;
  tsq: number; // Total sell quantity (Ask Volume)
  tbq: number; // Total buy quantity (Bid Volume)
  o: number;   // Open price
  c: number;   // Close price
  lc: number;
  uc: number;
  ltt: number; // Server timestamp for last trade
  oi: number;
  avg: number;
  v: number;
  h: number;
  l: number;
}
interface WebSocketMessage { Type: "Ticks"; PayLoad: TickData[]; }

export function usePriceWebSocket(
  symbols: TradingSymbol[],
  setSymbols: React.Dispatch<React.SetStateAction<TradingSymbol[]>>,
  isAuthenticated: boolean
) {
  const [isConnected, setIsConnected] = useState(false);
  const [reconnectingStatus, setReconnectingStatus] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const feedFallbackTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const visibilityReconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000;

  // Add refresh token state to prevent concurrent calls
  const [isRefreshingToken, setIsRefreshingToken] = useState(false);
  const refreshPromiseRef = useRef<Promise<string> | null>(null);

  // CLEANUP on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (feedFallbackTimeoutRef.current) {
        clearTimeout(feedFallbackTimeoutRef.current);
        feedFallbackTimeoutRef.current = null;
      }
      if (visibilityReconnectTimeoutRef.current) {
        clearTimeout(visibilityReconnectTimeoutRef.current);
        visibilityReconnectTimeoutRef.current = null;
      }
    };
  }, []);

  // Helper function to log connection attempts
  const logConnectionStatus = (message: string) => {
    console.info(message);
  };

  // Helper function to clear all reconnection timers
  const clearAllReconnectTimers = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (visibilityReconnectTimeoutRef.current) {
      clearTimeout(visibilityReconnectTimeoutRef.current);
      visibilityReconnectTimeoutRef.current = null;
    }
  }, []);

  // Token refresh for WebSocket
  const refreshTokenForWebSocket = async (): Promise<string> => {
    // If already refreshing, return the existing promise
    if (isRefreshingToken && refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    setIsRefreshingToken(true);
    refreshPromiseRef.current = performTokenRefreshForWebSocket();
    
    try {
      const newAccessToken = await refreshPromiseRef.current;
      return newAccessToken;
    } finally {
      setIsRefreshingToken(false);
      refreshPromiseRef.current = null;
    }
  };

  const performTokenRefreshForWebSocket = async (): Promise<string> => {
    const refreshToken = localStorage.getItem('refresh_token');
    
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      console.log('WebSocket: Refreshing token...');
      const response = await fetch(`${config.apiUrl}refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();
      
      // Update localStorage with new tokens
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      
      console.log('WebSocket: Token refreshed successfully');
      return data.access_token;
    } catch (error) {
      console.error('WebSocket: Token refresh failed:', error);
      // Clear all tokens and dispatch event to notify AuthContext
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      
      // Dispatch custom event to notify AuthContext
      window.dispatchEvent(new CustomEvent('tokenRefreshFailed'));
      
      throw error;
    }
  };

  // Get valid access token for WebSocket
  const getValidAccessTokenForWebSocket = async (): Promise<string> => {
    let accessToken = localStorage.getItem('access_token');
    
    if (!accessToken) {
      throw new Error('No access token available');
    }

    // Check if token is expired or expiring soon
    if (isTokenExpired(accessToken) || isTokenExpiringSoon(accessToken)) {
      console.log('WebSocket: Access token expired or expiring soon, refreshing...');
      accessToken = await refreshTokenForWebSocket();
    }

    return accessToken;
  };

  // Updated helper function to accept negative values as valid
  const preserveLastKnownValue = (newValue: number, currentValue: number): number => {
    // Accept any valid number (positive or negative), only reject null, undefined, or 0
    if (newValue !== null && newValue !== undefined && newValue !== 0) {
      return newValue;
    }
    // If new value is 0, null, or undefined, preserve current value if it's valid
    if (currentValue !== null && currentValue !== undefined && currentValue !== 0) {
      return currentValue;
    }
    // Fallback to current value or 0
    return currentValue || 0;
  };

  // Function to fetch feed data as fallback when WebSocket is not connected
  const fetchFeedDataFallback = useCallback(async () => {
    if (!isAuthenticated || isConnected) return;
    
    try {
      const accessToken = await getValidAccessTokenForWebSocket();
      
      console.log("Fetching feed data as fallback...");
      const freshSymbols = await fetchSymbolsFromAPI(accessToken);
      
      // Update symbols with fresh feed data, preserving display names
      setSymbols(prevSymbols => 
        prevSymbols.map(prevSymbol => {
          const freshData = freshSymbols.find(fs => 
            fs.symbol === prevSymbol.symbol || fs.name === prevSymbol.name
          );
          
          if (freshData) {
            return {
              ...prevSymbol,
              // Preserve the display name but update with fresh feed data
              name: prevSymbol.name, // Keep original display name
              buyPrice: freshData.buyPrice > 0 ? freshData.buyPrice : prevSymbol.buyPrice,
              sellPrice: freshData.sellPrice > 0 ? freshData.sellPrice : prevSymbol.sellPrice,
              high: freshData.high > 0 ? freshData.high : prevSymbol.high,
              low: freshData.low > 0 ? freshData.low : prevSymbol.low,
              open: freshData.open > 0 ? freshData.open : prevSymbol.open,
              close: freshData.close > 0 ? freshData.close : prevSymbol.close,
              spread: freshData.spread > 0 ? freshData.spread : prevSymbol.spread,
              change: freshData.change !== 0 ? freshData.change : prevSymbol.change,
              bidVolume: freshData.bidVolume > 0 ? freshData.bidVolume : prevSymbol.bidVolume,
              askVolume: freshData.askVolume > 0 ? freshData.askVolume : prevSymbol.askVolume,
              lastTradeTime: freshData.lastTradeTime || prevSymbol.lastTradeTime,
              _feedData: freshData._feedData
            };
          }
          return prevSymbol;
        })
      );
    } catch (error) {
      console.error("Failed to fetch feed fallback data:", error);
    }
  }, [isAuthenticated, isConnected, setSymbols]);

  const connectWebSocket = useCallback(async () => {
    // Clear any existing reconnection timers
    clearAllReconnectTimers();
    
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    if (!isAuthenticated) {
      return;
    }
    
    try {
      // Get valid access token (with refresh if needed)
      const accessToken = await getValidAccessTokenForWebSocket();
      
      // Show reconnecting status if this is a reconnection attempt
      if (reconnectAttempts.current > 0) {
        setReconnectingStatus(true);
        logConnectionStatus(`Establishing new WebSocket connection (Attempt ${reconnectAttempts.current + 1})`);
      }
      
      const ws = new WebSocket(`${WS_URL}?token=${encodeURIComponent(accessToken)}`);
      
      ws.onopen = () => {
        setIsConnected(true);
        setReconnectingStatus(false);
        reconnectAttempts.current = 0;
        clearAllReconnectTimers();
        logConnectionStatus("WebSocket connection established successfully");
        
        // Clear any feed fallback timers since WebSocket is connected
        if (feedFallbackTimeoutRef.current) {
          clearTimeout(feedFallbackTimeoutRef.current);
          feedFallbackTimeoutRef.current = null;
        }
      };
      
      ws.onmessage = (event) => {
        try {
          const data: WebSocketMessage = JSON.parse(event.data);
          if (data.Type === "Ticks" && Array.isArray(data.PayLoad)) {
            data.PayLoad.forEach((symbolData) => {
              setSymbols(prevSymbols =>
                prevSymbols.map(symbol => {
                  // Match using symbol field (not name) for WebSocket data
                  if (symbol.symbol === symbolData.sm) {
                    // Accept any valid number including negatives - preserve only for 0/null/undefined
                    const buyPrice = preserveLastKnownValue(symbolData.b, symbol.buyPrice);
                    const sellPrice = preserveLastKnownValue(symbolData.a, symbol.sellPrice);
                    const high = preserveLastKnownValue(symbolData.h, symbol.high);
                    const low = preserveLastKnownValue(symbolData.l, symbol.low);
                    const openPrice = preserveLastKnownValue(symbolData.o, symbol.openPrice || 0);
                    const closePrice = preserveLastKnownValue(symbolData.c, symbol.closePrice || 0);
                    const bidVolume = preserveLastKnownValue(symbolData.tbq, symbol.bidVolume || 0);
                    const askVolume = preserveLastKnownValue(symbolData.tsq, symbol.askVolume || 0);
                    const volume = preserveLastKnownValue(symbolData.v, symbol.volume || 0);
                    const openInterest = preserveLastKnownValue(symbolData.oi, symbol.openInterest || 0);
                    const open = preserveLastKnownValue(symbolData.o, symbol.open);
                    const close = preserveLastKnownValue(symbolData.c, symbol.close);
                    
                    // Calculate spread only if both prices are valid (can be negative)
                    const spread = (buyPrice !== 0 && sellPrice !== 0) ? Math.abs(sellPrice - buyPrice) : symbol.spread;
                    const change = symbolData.chp !== undefined ? symbolData.chp : symbol.change;
                    
                    // Use the server timestamp or current time if not available
                    const lastTradeTime = symbolData.ltt || Date.now();
                    
                    // Track if values have actually changed (not just preserved)
                    const buyPriceChanged = symbolData.b !== 0 && symbolData.b !== null && symbolData.b !== undefined && symbolData.b !== symbol.buyPrice;
                    const sellPriceChanged = symbolData.a !== 0 && symbolData.a !== null && symbolData.a !== undefined && symbolData.a !== symbol.sellPrice;
                    const highChanged = symbolData.h !== 0 && symbolData.h !== null && symbolData.h !== undefined && symbolData.h !== symbol.high;
                    const lowChanged = symbolData.l !== 0 && symbolData.l !== null && symbolData.l !== undefined && symbolData.l !== symbol.low;
                    const openChanged = symbolData.o !== 0 && symbolData.o !== null && symbolData.o !== undefined && symbolData.o !== symbol.open;
                    const closeChanged = symbolData.c !== 0 && symbolData.c !== null && symbolData.c !== undefined && symbolData.c !== symbol.close;
                    const bidVolumeChanged = symbolData.tbq !== 0 && symbolData.tbq !== null && symbolData.tbq !== undefined && symbolData.tbq !== symbol.bidVolume;
                    const askVolumeChanged = symbolData.tsq !== 0 && symbolData.tsq !== null && symbolData.tsq !== undefined && symbolData.tsq !== symbol.askVolume;
                    const volumeChanged = symbolData.v !== 0 && symbolData.v !== null && symbolData.v !== undefined && symbolData.v !== symbol.volume;
                    
                    return {
                      ...symbol,
                      // Keep the display name unchanged
                      name: symbol.name,
                      buyPrice,
                      sellPrice,
                      previousBuyPrice: buyPriceChanged ? symbol.buyPrice : symbol.previousBuyPrice,
                      previousSellPrice: sellPriceChanged ? symbol.sellPrice : symbol.previousSellPrice,
                      previousHigh: highChanged ? symbol.high : symbol.previousHigh,
                      previousLow: lowChanged ? symbol.low : symbol.previousLow,
                      openPrice,
                      closePrice,
                      spread,
                      change: typeof change === 'number' ? parseFloat(change.toFixed(2)) : symbol.change,
                      high: high,
                      low: low,
                      lastTradeTime: lastTradeTime,
                      bidVolume: bidVolume,
                      askVolume: askVolume,
                      previousBidVolume: bidVolumeChanged ? symbol.bidVolume || 0 : symbol.previousBidVolume,
                      previousAskVolume: askVolumeChanged ? symbol.askVolume || 0 : symbol.previousAskVolume,
                      open: open,
                      close: close,
                      openInterest: openInterest,
                      volume: volume,
                      // Track if values were preserved (no actual change or incoming was 0/invalid)
                      pricePreserved: {
                        buyPrice: (symbolData.b === 0 || symbolData.b === null || symbolData.b === undefined) && (symbol.buyPrice !== 0 && symbol.buyPrice !== null && symbol.buyPrice !== undefined),
                        sellPrice: (symbolData.a === 0 || symbolData.a === null || symbolData.a === undefined) && (symbol.sellPrice !== 0 && symbol.sellPrice !== null && symbol.sellPrice !== undefined),
                        high: (symbolData.h === 0 || symbolData.h === null || symbolData.h === undefined) && (symbol.high !== 0 && symbol.high !== null && symbol.high !== undefined),
                        low: (symbolData.l === 0 || symbolData.l === null || symbolData.l === undefined) && (symbol.low !== 0 && symbol.low !== null && symbol.low !== undefined),
                        open: (symbolData.o === 0 || symbolData.o === null || symbolData.o === undefined) && (symbol.open !== 0 && symbol.open !== null && symbol.open !== undefined),
                        close: (symbolData.c === 0 || symbolData.c === null || symbolData.c === undefined) && (symbol.close !== 0 && symbol.close !== null && symbol.close !== undefined),
                        bidVolume: (symbolData.tbq === 0 || symbolData.tbq === null || symbolData.tbq === undefined) && ((symbol.bidVolume || 0) !== 0),
                        askVolume: (symbolData.tsq === 0 || symbolData.tsq === null || symbolData.tsq === undefined) && ((symbol.askVolume || 0) !== 0),
                        volume: (symbolData.v === 0 || symbolData.v === null || symbolData.v === undefined) && ((symbol.volume || 0) !== 0),
                      }
                    };
                  }
                  return symbol;
                })
              );
            });
          }
        } catch (err) {
          console.error("Error parsing WebSocket message:", err);
        }
      };
      
      ws.onerror = (error) => {
        logConnectionStatus(`WebSocket error: ${JSON.stringify(error)}`);
        setIsConnected(false);
        setReconnectingStatus(true);
      };
      
      ws.onclose = (event) => {
        logConnectionStatus(`WebSocket connection closed with code: ${event.code}, reason: ${event.reason}`);
        setIsConnected(false);
        
        // Check if close was due to authentication error
        if (event.code === 1006 || event.code === 1008) {
          console.log('WebSocket: Connection closed, possibly due to invalid token');
        }
        
        // Only attempt reconnection if authenticated and not already reconnecting
        if (isAuthenticated && !reconnectTimeoutRef.current) {
          setReconnectingStatus(true);
          
          if (reconnectAttempts.current < maxReconnectAttempts) {
            // Exponential backoff: increase delay with each attempt
            const adjustedDelay = reconnectDelay * Math.pow(1.5, reconnectAttempts.current);
            reconnectAttempts.current++;
            
            reconnectTimeoutRef.current = setTimeout(() => {
              reconnectTimeoutRef.current = null;
              connectWebSocket();
            }, adjustedDelay);
            logConnectionStatus(`Scheduling reconnection in ${adjustedDelay}ms`);
          } else {
            // After max attempts, show a toast and stop reconnecting
            setReconnectingStatus(false);
            toast.error("Having trouble connecting. Please refresh the page.", {
              duration: 0, // Keep the toast until dismissed
              action: {
                label: "Refresh",
                onClick: () => window.location.reload()
              }
            });
          }
        }
      };
      
      wsRef.current = ws;
    } catch (error) {
      console.error(`WebSocket: Error creating connection or refreshing token: ${error}`);
      setIsConnected(false);
      setReconnectingStatus(true);
      
      // If token refresh failed, don't attempt reconnection
      if (error?.message?.includes('No refresh token') || error?.message?.includes('Token refresh failed')) {
        console.log('WebSocket: Token refresh failed, stopping reconnection attempts');
        return;
      }
      
      // Attempt reconnection after a delay for other errors
      if (reconnectAttempts.current < maxReconnectAttempts && isAuthenticated && !reconnectTimeoutRef.current) {
        reconnectAttempts.current++;
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectTimeoutRef.current = null;
          connectWebSocket();
        }, reconnectDelay);
      }
    }
  }, [isAuthenticated, setSymbols, clearAllReconnectTimers]);

  // Connect after symbols initialized & authenticated
  useEffect(() => {
    if (isAuthenticated && symbols.length > 0) {
      connectWebSocket();
    }
    // Cleanup on logout
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      clearAllReconnectTimers();
      // Reset refresh state
      setIsRefreshingToken(false);
      refreshPromiseRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, symbols.length]);
  
  // Enhanced visibility change detection with proper reconnection strategy
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        logConnectionStatus("Page visibility changed to visible");
        
        // Clear any existing visibility reconnect timer
        if (visibilityReconnectTimeoutRef.current) {
          clearTimeout(visibilityReconnectTimeoutRef.current);
          visibilityReconnectTimeoutRef.current = null;
        }
        
        // Check if we need to reconnect after a short delay
        // This prevents immediate reconnection attempts that might interfere with existing connections
        visibilityReconnectTimeoutRef.current = setTimeout(() => {
          visibilityReconnectTimeoutRef.current = null;
          
          if (isAuthenticated && symbols.length > 0) {
            // Check if websocket needs reconnection
            const needsReconnection = !wsRef.current || 
              wsRef.current.readyState === WebSocket.CLOSED || 
              wsRef.current.readyState === WebSocket.CLOSING;
            
            if (needsReconnection) {
              logConnectionStatus("WebSocket needs reconnection after visibility change");
              
              // Reset reconnect attempts for fresh start after visibility change
              reconnectAttempts.current = 0;
              setReconnectingStatus(false); // Reset status first
              
              // Start reconnection with periodic retries until success
              const attemptReconnection = () => {
                if (!isAuthenticated || symbols.length === 0) return;
                
                const currentNeedsReconnection = !wsRef.current || 
                  wsRef.current.readyState === WebSocket.CLOSED || 
                  wsRef.current.readyState === WebSocket.CLOSING;
                
                if (currentNeedsReconnection) {
                  logConnectionStatus("Attempting WebSocket reconnection after visibility change");
                  connectWebSocket();
                  
                  // Schedule next attempt if still needed
                  visibilityReconnectTimeoutRef.current = setTimeout(() => {
                    const stillNeedsReconnection = !wsRef.current || 
                      wsRef.current.readyState === WebSocket.CLOSED || 
                      wsRef.current.readyState === WebSocket.CLOSING;
                    
                    if (stillNeedsReconnection && document.visibilityState === 'visible') {
                      attemptReconnection();
                    } else {
                      visibilityReconnectTimeoutRef.current = null;
                    }
                  }, 5000); // 5 second intervals as requested
                } else {
                  logConnectionStatus("WebSocket already connected, no reconnection needed");
                  visibilityReconnectTimeoutRef.current = null;
                }
              };
              
              attemptReconnection();
            } else {
              logConnectionStatus("WebSocket is already connected, no reconnection needed");
            }
          }
        }, 1000); // 1 second delay before checking connection status
      } else {
        // Tab became hidden - clear visibility reconnect timer to stop reconnection attempts
        if (visibilityReconnectTimeoutRef.current) {
          clearTimeout(visibilityReconnectTimeoutRef.current);
          visibilityReconnectTimeoutRef.current = null;
        }
        setReconnectingStatus(false); // Clear reconnecting status when tab becomes hidden
      }
    };
    
    // Add event listener for visibility change
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Clean up event listener on unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (visibilityReconnectTimeoutRef.current) {
        clearTimeout(visibilityReconnectTimeoutRef.current);
        visibilityReconnectTimeoutRef.current = null;
      }
    };
  }, [isAuthenticated, symbols.length, connectWebSocket]);

  // Manual reconnect function that can be exposed
  const reconnect = useCallback(() => {
    clearAllReconnectTimers();
    reconnectAttempts.current = 0;
    setReconnectingStatus(false);
    connectWebSocket();
  }, [connectWebSocket, clearAllReconnectTimers]);

  return { isConnected, reconnectingStatus, reconnect, connectWebSocket };
}
